import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, ShoppingBag, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ImageGallery({ images, name }) {
  const [current, setCurrent] = useState(0);
  if (!images || images.length === 0) return null;

  return (
    <div className="relative">
      <div className="aspect-square overflow-hidden bg-zinc-900">
        <img src={images[current]} alt={`${name} ${current + 1}`} className="w-full h-full object-cover" />
      </div>
      {images.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); setCurrent((c) => (c - 1 + images.length) % images.length); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white hover:bg-black/80 transition-colors" data-testid="gallery-prev">
            <ChevronLeft size={16} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setCurrent((c) => (c + 1) % images.length); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white hover:bg-black/80 transition-colors" data-testid="gallery-next">
            <ChevronRight size={16} />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, i) => (
              <button key={i} onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
                className={`w-1.5 h-1.5 rounded-full transition-all ${i === current ? "bg-white w-3" : "bg-white/40"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function getAllImages(product) {
  const imgs = [];
  if (product.image_url) imgs.push(product.image_url);
  if (product.additional_images?.length) imgs.push(...product.additional_images);
  return imgs;
}

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  if (products.length === 0) return null;

  const orderViaWhatsApp = (product, e) => {
    if (e) e.stopPropagation();
    const msg = encodeURIComponent(`Hi! I'm interested in ordering:\n*${product.name}*\nPrice: Rs.${product.price}\n\nPlease share the details.`);
    window.open(`https://wa.me/919153020904?text=${msg}`, "_blank");
  };

  return (
    <section id="shop" className="py-8 md:py-10" data-testid="shop-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {products.map((p, i) => (
            <motion.div
              key={p.id || i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              onClick={() => setSelected(p)}
              className="border border-white/10 card-lift group bg-gg-black overflow-hidden rounded-lg cursor-pointer"
              data-testid={`product-card-${i}`}
            >
              {p.image_url && (
                <div className="aspect-square overflow-hidden bg-zinc-900">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-5">
                <h3 className="font-heading text-sm uppercase tracking-wide font-bold text-white group-hover:text-gg-red transition-colors">{p.name}</h3>
                <span className="font-heading text-gg-red text-lg font-bold mt-1 block">Rs.{p.price}</span>
                <p className="text-zinc-500 text-xs mt-1.5 leading-relaxed line-clamp-2">{p.description}</p>
                <div className="mt-3 text-[10px] font-heading uppercase tracking-widest text-zinc-500 group-hover:text-white transition-colors">Tap for details</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Product Detail Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gg-surface border border-white/10 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto relative"
              data-testid="product-modal"
            >
              <button onClick={() => setSelected(null)} className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-zinc-400 hover:text-white transition-colors" data-testid="product-modal-close">
                <X size={16} />
              </button>

              <ImageGallery images={getAllImages(selected)} name={selected.name} />

              <div className="p-6">
                <div className="flex items-center gap-1.5 mb-2">
                  <ShoppingBag size={12} className="text-gg-red" />
                  <span className="text-[9px] font-heading uppercase tracking-widest text-gg-red">GG Store</span>
                </div>
                <h3 className="font-heading text-xl uppercase tracking-wide font-bold text-white">{selected.name}</h3>
                <span className="font-heading text-gg-red text-2xl font-bold mt-1 block">Rs.{selected.price}</span>
                <p className="text-zinc-400 text-sm mt-3 leading-relaxed">{selected.description}</p>

                <div className="mt-5 flex gap-3">
                  <Button onClick={(e) => orderViaWhatsApp(selected, e)}
                    className="flex-1 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-lg font-heading uppercase tracking-widest text-xs h-11 transition-all duration-300 hover:shadow-lg hover:shadow-[#25D366]/20 btn-press"
                    data-testid="product-modal-order">
                    <MessageCircle size={16} className="mr-2" /> Order via WhatsApp
                  </Button>
                </div>
                <p className="text-zinc-600 text-[10px] text-center mt-3">Orders are placed via WhatsApp for quick confirmation</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
